import { schedule } from 'owa-task-queue/lib/schedule';
import { addToTimingMap } from 'owa-performance/lib/utils/timingMap';
import { isFeatureEnabled } from 'owa-feature-flags';

export type OwaEntryType =
    | 'delayed-post-message'
    | 'element'
    | 'event'
    | 'layout-shift'
    | 'long-animation-frame'
    | 'longtask'
    | 'paint';

type PerformanceObserverCallback = (entry: PerformanceEntryList) => void;
interface PerformanceObserverWrapper {
    source: string;
    callback: PerformanceObserverCallback;
}

const performanceObserverCbs: {
    [_ in OwaEntryType]: PerformanceObserverWrapper[] | undefined;
} = {
    'delayed-post-message': undefined,
    element: undefined,
    event: undefined,
    'layout-shift': undefined,
    'long-animation-frame': undefined,
    longtask: undefined,
    paint: undefined,
};

export function addPerfObserverCallback(
    source: string,
    type: OwaEntryType,
    cb: PerformanceObserverCallback
): (() => void) | undefined {
    if (
        'PerformanceObserver' in self &&
        'supportedEntryTypes' in self.PerformanceObserver &&
        PerformanceObserver.supportedEntryTypes.includes(type)
    ) {
        let wrapper = performanceObserverCbs[type];
        if (!wrapper) {
            wrapper = performanceObserverCbs[type] = [];
            const observer = new PerformanceObserver((list: PerformanceObserverEntryList) => {
                if (wrapper) {
                    const entries = list.getEntries();
                    if (entries.length > 0) {
                        for (const { callback, source: wrapperSource } of wrapper) {
                            schedule(() => {
                                addToTimingMap('schedule', `perfObserver:${wrapperSource}`);
                                callback(entries);
                            });
                        }
                    }
                }
            });

            const options: ExtendedPerformanceObserverInit = { type };
            if (type === 'event' && isFeatureEnabled('fwk-event-timing-threshold-50ms')) {
                options.durationThreshold = 50;
            }

            observer.observe(options);
        }

        wrapper.push({
            callback: cb,
            source,
        });

        return () => {
            if (wrapper) {
                const idx = wrapper.findIndex(w => w.source === source);
                wrapper.splice(idx, 1);
            }
        };
    }
    return undefined;
}

export function isPerfObserverTypeSupported(type: OwaEntryType): boolean {
    return (
        'PerformanceObserver' in self &&
        'supportedEntryTypes' in self.PerformanceObserver &&
        PerformanceObserver.supportedEntryTypes.includes(type)
    );
}
